/**
 * Shared two-image transition engine.
 * The user-facing product is Staging Animation (transition_type = staging).
 * Additional transition types can be added later without a second pipeline.
 */

export const TRANSITION_TYPES = ['staging'] as const;
export type TransitionType = (typeof TRANSITION_TYPES)[number];

export const STAGING_ANIMATION_STYLES = [
  'furniture_build',
  'soft_reveal',
  'cinematic_reveal',
  'pop_in',
  'sweep_reveal',
  'slide_in',
  'furniture_removal',
  'people_placing',
] as const;
export type StagingAnimationStyle = (typeof STAGING_ANIMATION_STYLES)[number];

export const STAGING_ANIMATION_DURATIONS = [5, 7, 10] as const;
export type StagingAnimationDuration = (typeof STAGING_ANIMATION_DURATIONS)[number];

export const STAGING_ANIMATION_RATIOS = ['original', '16:9', '9:16', '1:1'] as const;
export type StagingAnimationRatio = (typeof STAGING_ANIMATION_RATIOS)[number];

export const STAGING_ANIMATION_MOTION = ['subtle', 'standard', 'dynamic'] as const;
export type StagingAnimationMotion = (typeof STAGING_ANIMATION_MOTION)[number];

export const STAGING_ANIMATION_SOURCES = ['imagemotion', 'virtual_staging'] as const;
export type StagingAnimationSource = (typeof STAGING_ANIMATION_SOURCES)[number];

export const DEFAULT_STAGING_ANIMATION_CREDIT_COST = 5;

export interface StagingAnimationStyleOption {
  id: StagingAnimationStyle;
  label: string;
  description: string;
  recommended?: boolean;
}

export const STAGING_ANIMATION_STYLE_OPTIONS: StagingAnimationStyleOption[] = [
  {
    id: 'furniture_build',
    label: 'Furniture Build',
    description: 'Pieces appear one at a time, largest first, and settle in place.',
    recommended: true,
  },
  {
    id: 'soft_reveal',
    label: 'Soft Reveal',
    description: 'Everything fades in together. No sliding and no camera move.',
  },
  {
    id: 'cinematic_reveal',
    label: 'Cinematic Reveal',
    description: 'One slow push-in while the room fades into the staged photo.',
  },
  {
    id: 'pop_in',
    label: 'Pop into Place',
    description: 'Each piece pops into its final spot, one at a time. The room stays still.',
  },
  {
    id: 'sweep_reveal',
    label: 'Sweep Reveal',
    description: 'A single wipe reveals the finished room. The camera stays locked.',
  },
  {
    id: 'slide_in',
    label: 'Slide In',
    description: 'Furniture glides in along the floor and stops in its final place.',
  },
  {
    id: 'furniture_removal',
    label: 'Furniture Removal',
    description: 'Pieces leave one at a time until the room matches the empty photo.',
  },
  {
    id: 'people_placing',
    label: 'People Placing',
    description: 'Movers carry each piece in, set it down, then leave the room.',
  },
];

export const RATIO_OPTIONS: { id: StagingAnimationRatio; label: string }[] = [
  { id: 'original', label: 'Original' },
  { id: '16:9', label: 'Landscape' },
  { id: '9:16', label: 'Portrait' },
  { id: '1:1', label: 'Square' },
];

export const MOTION_OPTIONS: { id: StagingAnimationMotion; label: string }[] = [
  { id: 'subtle', label: 'Subtle' },
  { id: 'standard', label: 'Standard' },
  { id: 'dynamic', label: 'Dynamic' },
];

export interface TransitionAnimationSettings {
  enabled: boolean;
  credit_cost: number;
  default_duration: StagingAnimationDuration;
  allowed_durations: number[];
  available_styles: StagingAnimationStyle[];
  default_style: StagingAnimationStyle;
  available_aspect_ratios: StagingAnimationRatio[];
  default_motion: StagingAnimationMotion;
  provider: string;
  model_endpoint: string;
  adapter_type: string;
}

export const DEFAULT_TRANSITION_SETTINGS: TransitionAnimationSettings = {
  enabled: true,
  credit_cost: DEFAULT_STAGING_ANIMATION_CREDIT_COST,
  default_duration: 7,
  allowed_durations: [5, 7, 10],
  available_styles: [...STAGING_ANIMATION_STYLES],
  default_style: 'furniture_build',
  available_aspect_ratios: [...STAGING_ANIMATION_RATIOS],
  default_motion: 'standard',
  provider: 'fal',
  model_endpoint: 'fal-ai/veo3.1/fast/first-last-frame-to-video',
  adapter_type: 'veo_first_last',
};

const ROOM_RULES =
  'Do not invent furniture, paintings, pictures, mirrors, posters, plants, lamps, curtains, blinds, rugs, or decor. ' +
  'Windows, glass, doors, and the view through them stay clear and unchanged for the entire clip. ' +
  'Never hang a painting, picture, mirror, or any object over a window, door, or pane of glass unless that exact object already covers that exact opening in the furnished photograph. ' +
  'Do not add walls. Do not remove, move, extend, or rebuild walls. Walls, floors, ceilings, windows, doors, cabinetry, and the camera viewpoint stay identical to the photographs for every frame. ' +
  'The room itself never changes. No text or watermarks. No warped or melting objects.';

const SCENE_RULES =
  'One continuous shot of this one room from the first frame to the last. No cuts, no scene changes, no other rooms, no new location, and no redesigned or imagined version of the space in between. ' +
  'Every in-between frame shows this same room from this same viewpoint, with the same lighting, colours, and time of day. ' +
  'Anything that appears in both photographs stays exactly where it is and does not move, fade, flicker, or change. ';

const APPEAR_BASE =
  'This is a transition between two photographs of the same room. The first frame is the empty room. The last frame is the furnished reference and is the only allowed result. ' +
  SCENE_RULES +
  'Show only objects that are already visible in the furnished reference, in the same design, size, count, and position. ' +
  'If an object is not in the furnished reference, it must never appear at any point in the video. ' +
  ROOM_RULES +
  ' Only the furniture and decor that are in the furnished reference and missing from the empty room animate into place. Everything else stays still. No people. ' +
  'End on a stable frame that matches the furnished reference.';

const REMOVAL_BASE =
  'This is a transition between two photographs of the same room. The first frame is the furnished room. The last frame is the empty room and is the only allowed result. ' +
  SCENE_RULES +
  'Remove only objects that are visible in the furnished photograph and absent from the empty room. Do not leave furniture behind, and do not add any new object. ' +
  ROOM_RULES +
  ' Everything that is not being removed stays still. No people. End on a stable frame that matches the empty room.';

const PEOPLE_BASE =
  'This is a transition between two photographs of the same room. The first frame is the empty room. The last frame is the furnished reference and is the only allowed result. ' +
  SCENE_RULES +
  'Show only objects that are already visible in the furnished reference, in the same design, size, count, and position. ' +
  'If an object is not in the furnished reference, it must never appear at any point in the video. ' +
  ROOM_RULES +
  ' Adult movers may enter only to carry that furniture in and set it down. They must leave before the end. ' +
  'The final frame has no people and matches the furnished reference.';

function basePromptFor(style: StagingAnimationStyle): string {
  if (style === 'furniture_removal') return REMOVAL_BASE;
  if (style === 'people_placing') return PEOPLE_BASE;
  return APPEAR_BASE;
}

const STYLE_PROMPTS: Record<StagingAnimationStyle, string> = {
  furniture_build:
    'Style: furniture build. Camera locked. Objects from the furnished reference appear one at a time, largest first, then smaller pieces. Each one settles into its exact final position and stops. Do not fade the whole room at once.',
  soft_reveal:
    'Style: soft reveal. Camera locked. Every object from the furnished reference fades in together, already in its final position. No sliding, no wipe, and no camera move.',
  cinematic_reveal:
    'Style: cinematic reveal. One very slow push-in only, while objects from the furnished reference fade in together. No pan, no orbit, and no parallax. Architecture stays rigid.',
  pop_in:
    'Style: pop into place. Camera locked. Each object from the furnished reference pops into its exact final position, one at a time, largest first. The pop is a quick appearance in place, not a fade of the whole room and not a slide from off screen. No bounce, no squash, and no morphing.',
  sweep_reveal:
    'Style: sweep reveal. Camera locked. A single hard wipe crosses the frame. One side is the empty room and the other is the finished furnished reference. Do not animate objects individually and do not morph.',
  slide_in:
    'Style: slide in. Camera locked. Furniture from the furnished reference glides a short distance along the floor into its exact final position, largest pieces first. No fade of the whole room and no wipe.',
  furniture_removal:
    'Style: furniture removal. Camera locked. Objects leave one at a time, largest first, until the room matches the empty photograph. Each piece lifts away from where it stands. Do not fade the walls or the whole room at once.',
  people_placing:
    'Style: people placing furniture. Camera locked. Two adult movers in plain work clothes carry in only the furniture from the furnished reference and set each piece down in its exact final position, largest first. They do not bring extra objects and they do not change the room. When every piece is in place, both movers walk out of frame. The last moment is still, with nobody left in the room.',
};

const MOTION_PROMPTS: Record<StagingAnimationMotion, string> = {
  subtle: 'Motion level: subtle. Keep movement minimal and believable for property photography.',
  standard: 'Motion level: standard. Smooth, professional, and believable.',
  dynamic:
    'Motion level: dynamic, but still photoreal. Do not distort furniture, walls, or perspective.',
};

export function buildStagingAnimationPrompt(input: {
  style: StagingAnimationStyle;
  motion: StagingAnimationMotion;
}): string {
  return [basePromptFor(input.style), STYLE_PROMPTS[input.style], MOTION_PROMPTS[input.motion]].join(' ');
}

/** Removal plays furnished → empty, so the provider start and end frames are swapped. */
export function stagingAnimationReversesFrames(style: StagingAnimationStyle): boolean {
  return style === 'furniture_removal';
}

const NEGATIVE_SHARED =
  'invented objects, extra furniture, added walls, new walls, extra walls, wall construction, changing walls, rebuilt room, painting over a window, picture covering a window, artwork on glass, extra wall art, hallucinated decor, new curtains, new blinds, extra plants, extra lamps, text, watermark, warped furniture, melting objects, morphing architecture, scene change, camera cut, different room, new location, imagined interior, redesigned room, intermediate scene, lighting change, time of day change';

export const STAGING_ANIMATION_NEGATIVE_PROMPT = `${NEGATIVE_SHARED}, people, cartoon`;

export function buildStagingAnimationNegativePrompt(style: StagingAnimationStyle): string {
  if (style === 'people_placing') {
    return `${NEGATIVE_SHARED}, children, crowd, extra people, people remaining in the final frame, distorted faces, cartoon`;
  }
  return STAGING_ANIMATION_NEGATIVE_PROMPT;
}

export interface ImageMatchWarning {
  code: 'aspect' | 'orientation' | 'size' | 'viewpoint';
  message: string;
}

export interface ImageMatchResult {
  warnings: ImageMatchWarning[];
  title: string | null;
  body: string | null;
}

/** Compare two source photos before generation. Never blocks generation. */
export function compareTransitionImages(
  before: { width: number; height: number } | null,
  after: { width: number; height: number } | null,
): ImageMatchResult {
  const warnings: ImageMatchWarning[] = [];
  if (!before || !after || before.width < 2 || after.width < 2) {
    return { warnings, title: null, body: null };
  }

  const beforeLandscape = before.width >= before.height;
  const afterLandscape = after.width >= after.height;
  if (beforeLandscape !== afterLandscape) {
    warnings.push({
      code: 'orientation',
      message: 'The two images have different orientations.',
    });
  }

  const beforeRatio = before.width / before.height;
  const afterRatio = after.width / after.height;
  const ratioDelta = Math.abs(beforeRatio - afterRatio) / Math.max(beforeRatio, afterRatio);
  if (ratioDelta > 0.08) {
    warnings.push({
      code: 'aspect',
      message: 'Aspect ratios differ.',
    });
  }

  const widthDelta = Math.abs(before.width - after.width) / Math.max(before.width, after.width);
  const heightDelta = Math.abs(before.height - after.height) / Math.max(before.height, after.height);
  if (widthDelta > 0.2 || heightDelta > 0.2) {
    warnings.push({
      code: 'size',
      message: 'Pixel dimensions differ substantially.',
    });
  }

  if (warnings.length === 0) return { warnings, title: null, body: null };
  return {
    warnings,
    title: 'Images may not match',
    body: 'For the best result, use the same photograph before and after virtual staging.',
  };
}

export function isStagingAnimationStyle(value: string): value is StagingAnimationStyle {
  return (STAGING_ANIMATION_STYLES as readonly string[]).includes(value);
}

export function isStagingAnimationRatio(value: string): value is StagingAnimationRatio {
  return (STAGING_ANIMATION_RATIOS as readonly string[]).includes(value);
}

export function isStagingAnimationMotion(value: string): value is StagingAnimationMotion {
  return (STAGING_ANIMATION_MOTION as readonly string[]).includes(value);
}
